import { useCallback, useEffect, useRef, useState } from "react"

export interface ISessionHealth {
  status: "ok" | "expired" | "error" | "unknown"
  expiresAt: Date | null
  hoursRemaining: number | null
  lastChecked: Date | null
}

interface IWorkerMessage {
  type: "session_ok" | "session_expired" | "session_error"
  expiresAt?: string | null
}

const INITIAL_STATE: ISessionHealth = {
  status: "unknown",
  expiresAt: null,
  hoursRemaining: null,
  lastChecked: null,
}

function deriveHoursRemaining(expiresAt: Date | null): number | null {
  if (!expiresAt) return null
  const ms = expiresAt.getTime() - Date.now()
  return ms > 0 ? ms / (1000 * 60 * 60) : 0
}

/**
 * Spawns a Web Worker that pings /api/auth/session every 30 minutes.
 * Returns live session health so components can react to expiry.
 * Worker is terminated on unmount.
 */
export function useSessionKeepalive(): ISessionHealth {
  const [health, setHealth] = useState<ISessionHealth>(INITIAL_STATE)
  const workerRef = useRef<Worker | null>(null)

  const handleMessage = useCallback((event: MessageEvent<IWorkerMessage>): void => {
    const now = new Date()
    const { type, expiresAt: rawExpiry } = event.data

    if (type === "session_ok") {
      const expiresAt = rawExpiry ? new Date(rawExpiry) : null
      setHealth({
        status: "ok",
        expiresAt,
        hoursRemaining: deriveHoursRemaining(expiresAt),
        lastChecked: now,
      })
    } else if (type === "session_expired") {
      setHealth({
        status: "expired",
        expiresAt: null,
        hoursRemaining: 0,
        lastChecked: now,
      })
    } else {
      setHealth((prev) => ({
        ...prev,
        status: "error",
        lastChecked: now,
      }))
    }
  }, [])

  useEffect(() => {
    const worker = new Worker(new URL("../workers/session-keepalive.worker.ts", import.meta.url), {
      type: "module",
    })
    workerRef.current = worker
    worker.addEventListener("message", handleMessage)

    return (): void => {
      worker.removeEventListener("message", handleMessage)
      worker.terminate()
      workerRef.current = null
    }
  }, [handleMessage])

  return health
}
